<template>
  <div class="container py-5">

    <div class="level">
      <div class="buttons level-left mb-0">
        <button
          @click="addingProduct = true"
          class="button is-primary">
          Add a Product
        </button>

        <button
          @click="managingCategories = true"
          class="button">
          Manage Product Categories
        </button>

        <button
          @click="managingSubcategories = true"
          class="button">
          Manage Product Subcategories
        </button>

        <button class="button"
          v-if="selected.length > 0"
          @click="launchConfirmDelete(true)">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete {{ selected.length }} Selected Products</span>
        </button>
      </div>

      <div class="level-right">
        <div class="mx-5">
          <label class="help mt-0">Filter by Product Brand</label>
          <div class="control">
            <label class="radio">
              <input 
                type="radio" 
                name="Cladify products" 
                @input="setFilter({ field: 'product_brand', value: 'Cladify' })"
                :checked="filters.product_brand == 'Cladify'">
              Cladify
            </label>
            <label class="radio">
              <input 
                type="radio" 
                name="Mitrex products" 
                @input="setFilter({ field: 'product_brand', value: 'Mitrex' })"
                :checked="filters.product_brand == 'Mitrex'">
              Mitrex
            </label>
            <label class="radio">
              <input 
                type="radio" 
                name="Mitrex products" 
                @input="setFilter({ field: 'product_brand', value: '' })"
                :checked="filters.product_brand == ''">
              No Brand
            </label>
          </div>
        </div>
        <v-search 
          @search="setFilter({ field: 'name', value: $event })"
          @clear="setFilter({ field: 'name', value: null })">
        </v-search>
      </div>
    </div>

    <div class="tabs mt-0">
      <ul>
        <li 
          v-for="category in categories"
          @click="setFilter({
            field: 'product_category_id', 
            value: category.id
          })"
          :key="category.id"
          :class="{ 'is-active': category.id == filters.product_category_id }">
          <a>{{ category.category }}</a>
        </li>
      </ul>
    </div>

    <v-fade-transition mode="out-in">

      <article 
        v-if="!loading && products.length == 0" 
        class="message is-info is-fullwidth">
        <div class="message-body">
          There are no products.
        </div>
      </article>

      <table 
        v-if="!loading && products.length > 0"
        :key="filters.product_category_id"
        class="table is-fullwidth is-hoverable has-smallish-font">

        <thead>
          <tr>
            <th 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex">
              {{ header }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr 
            class="v-centered"
            v-for="product in products"
            :key="product.id">

            <td>
              <span v-if="product.product_category != null">
                {{ product.product_category.category }}
              </span>
              <span v-else class="has-text-grey-lighter">
                No product category
              </span>
            </td>
            <td>
              <span v-if="product.product_subcategory != null">
                {{ product.product_subcategory.subcategory }}
              </span>
              <span v-else class="has-text-grey-lighter">
                No product subcategory
              </span>
            </td>
            <td>
              {{ product.name }}
            </td>
            <td>
              {{ product.desc }}
            </td>
            <template v-if="selectedCategory.has_dimensions">
              <td>
                <div class="text--nowrap">
                  <span v-if="minLwt(product)">
                    {{ minLwt(product) }}
                  </span>
                  <span v-else class="has-text-grey-light">
                    N/A
                  </span>
                </div>
                <div class="text--nowrap">
                  <span v-if="maxLwt(product)">
                    {{ maxLwt(product) }}
                  </span>
                  <span v-else class="has-text-grey-light">
                    N/A
                  </span>
                </div>
              </td>
            </template>

            <td v-else>
              {{ product.size }}
            </td>

            <td>
              <span v-if="product.unit">
                {{ product.unit.unit_display_name }}
              </span>
              <span v-else class="has-text-grey-light">
                N/A
              </span>
            </td>

            <td>
              <span v-if="filters.product_brand == 'Mitrex'">
                {{ product.unit_price_w_install | currency(2) }}
              </span>
              <span v-else>
                {{ product.unit_price | currency(2) }}
              </span>
            </td>

            <td>
              <span v-if="filters.product_brand == 'Mitrex'">
                {{ product.unit_price_dpa | currency(2) }}
              </span>
              <span v-else>
                {{ product.unit_price_w_install | currency(2) }}
              </span>
            </td>

            <td v-if="selectedCategory.has_dimensions">
              <span v-if="filters.product_brand == 'Mitrex'">
                {{ product.linear_unit_price_w_install | currency(2) }}
              </span>
              <span v-else>
                {{ product.linear_unit_price | currency(2) }}
              </span>
            </td>

            <td v-if="selectedCategory.has_dimensions">
              <span v-if="filters.product_brand == 'Mitrex'">
                {{ product.linear_unit_price_dpa | currency(2) }}
              </span>
              <span v-else>
                {{ product.linear_unit_price_w_install | currency(2) }}
              </span>
            </td>

            <td>
              <a 
                @click="preview.src = product.image; preview.state = true"
                v-if="product.image">
                Sample Image
              </a>
              <span v-else class="has-text-grey-light">
                N/A
              </span>
            </td>

            <td>
              <div class="buttons buttons-right">
                <button 
                  @click="editProduct.product = product; editProduct.state = true"
                  class="button is-small has-text-grey"
                  title="edit product">
                  <span class="icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </button>
                <button 
                  @click="deleteId = product.id; launchConfirmDelete()"
                  class="button is-small has-text-grey"
                  title="delete product">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </button>
              </div>
            </td>

          </tr>
        </tbody>

      </table>
    </v-fade-transition>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <add-product-modal
      v-if="addingProduct"
      v-model="addingProduct"
      :product-category-id="filters.product_category_id"/>

    <edit-product-modal
      v-if="editProduct.product"
      v-model="editProduct.state"
      :product="editProduct.product"/>

    <manage-product-categories-modal 
      v-if="managingCategories"
      v-model="managingCategories"/>

    <manage-product-subcategories-modal
      v-if="managingSubcategories"
      v-model="managingSubcategories"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmed()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

    <v-file-preview
      v-model="preview.state"
      :src="preview.src"/>

  </div>
</template>

<script>
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VFilePreview from '@/components/base/VFilePreview'
import VSearch from '@/components/base/VSearch'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import AddProductModal from '@/components/inventory/AddProductModal'
import EditProductModal from '@/components/inventory/EditProductModal'
import ManageProductCategoriesModal from '@/components/inventory/ManageProductCategoriesModal'
import ManageProductSubcategoriesModal from '@/components/inventory/ManageProductSubcategoriesModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VConfirmModal, 
    VPagination, 
    VFilePreview,
    VSearch,
    VFadeTransition,
    AddProductModal,
    EditProductModal,
    ManageProductCategoriesModal,
    ManageProductSubcategoriesModal
  },
  data() {
    return {
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this product?',
        type: 'danger'
      },
      deleteId: null,
      bulk: false,
      addingProduct: false,
      managingCategories: false,
      managingSubcategories: false,
      preview: {
        state: false,
        src: null
      },
      editProduct: {
        state: false,
        product: null
      }
    }
  },
  computed: {
    ...mapState("products", [
      "products", "pagination", "selected", "loading",
      "categories", "subcategories", "unitTypes", "filters"
    ]),
    selectedCategory() {
      return this.categories.filter(
        d => d.id == this.filters.product_category_id
      )[0];
    },
    headers({ selectedCategory, filters }) {
      if (selectedCategory.has_dimensions) {
        if (filters.product_brand == 'Mitrex') {
          return [
            'Product Category', 'Product Subcategory', 'Name', 
            'Description/ Specs', 'Min. and Max. Dimensions"',
            'Unit', 'MPA Unit Price', 'DPA Unit Price', 'MPA Linear Unit price', 
            'DPA Linear Unit Price', 'Sample Image', ''
          ]
        } else {
          return [
            'Product Category', 'Product Subcategory', 'Name', 
            'Description/ Specs', 'Min. and Max. Dimensions"',
            'Unit', 'Unit Price', 'Unit Price w/ Install', 'Linear Unit price', 
            'Linear Unit Price w/ Install', 'Sample Image', ''
          ]
        }
      } else {
        if (filters.product_brand == 'Mitrex') {
          return [
            'Product Category', 'Product Subcategory', 'Name', 
            'Description/ Specs', 'Size', 'MPA Unit Price', 'DPA Unit Price', 
            'Sample Image', ''
          ]
        } else {
          return [
            'Product Category', 'Product Subcategory', 'Name', 
            'Description/ Specs', 'Size', 'Unit', 'Unit Price', 'Unit Price w/ Install',
            'Sample Image', ''
          ]
        }
      }
    }
  },
  watch: {
    'pagination.page'() {
      let params = {...this.pagination, ...this.filters};
      this.getProducts(params);
    },
    filters: {
      handler(filters) {
        this.getProducts(filters);
        this.getSubcategories(
          { product_category_id: filters.product_category_id }
        )
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("products", [
      "getProducts", "getCategories", "getSubcategories", 
      "deleteProduct", "updateProduct", "addProductImage"
    ]),
    ...mapMutations("products", ["changePage", "setFilter", "setLoading"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.categories.length == 0) {
        this.setLoading(true);
        this.getCategories().then(
          resp => {
            this.setFilter({ 
              field: 'product_category_id', 
              value: resp.data.results[0].id 
            });
          }
        )
      } else {
        this.setFilter({ 
          field: 'product_category_id', 
          value: this.categories[0].id 
        });
      }
    },
    minLwt(product) {
      if (!product.min_length_in_str && !product.min_width_in_str && !product.min_thick_in_str) {
        return false
      } else {
        return `L${product.min_length_in_str}" x W${product.min_width_in_str}" x T${product.min_thick_in_str}"`
      }
    },
    maxLwt(product) {
      if (!product.max_length_in_str && !product.max_width_in_str && !product.max_thick_in_str) {
        return false
      } else {
        return `L${product.max_length_in_str}" x W${product.max_width_in_str}" x T${product.max_thick_in_str}"`
      }  
    },
    launchConfirmDelete(bulk=false) {
      this.bulk = bulk;
      if (bulk) {
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} product?`;
      } else {
        this.confirmDelete.message = 'Are you sure you want to delete this product?'
      }
      this.confirmDelete.state = true;
    },
    deleteConfirmed() {
      this.confirmDelete.state = false;

      if (this.bulk == true) {
        this.deleteSelected();
      } else {
        this.deleteProduct(this.deleteId).then(
          () => this.deleteId = null
        );
      }
    },
    update(id, field, v) {
      let data = {};
      data[field] = v;

      this.updateProduct({ productId: id, data: data });
    },
    updateImage(id, v) {
      let formData = new FormData();
      formData.append('image', v.file, v.file.name);

      this.addProductImage({ productId: id, data: formData })
        .then(() => {
          this.setNotification({
            status: true,
            type: 'success',
            message: 'Product image successfully uploaded'
          })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>